import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const CommunicationTools = ({ caseload }) => {
  const [messageType, setMessageType] = useState('individual');
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [messageContent, setMessageContent] = useState('');

  const recipientOptions = caseload?.map(student => ({
    value: student?.id,
    label: `${student?.name} (${student?.grade})`
  }));

  const handleSendMessage = () => {
    console.log('Envoi message:', { messageType, selectedRecipients, messageContent });
    setMessageContent('');
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Direct Messaging */}
      <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Icon name="Mail" size={20} />
          Messagerie Directe
        </h3>

        <div className="space-y-4">
          <div className="flex gap-2">
            <button
              onClick={() => setMessageType('individual')}
              className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                messageType === 'individual' ?'bg-blue-500 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Message Individuel
            </button>
            <button
              onClick={() => setMessageType('group')}
              className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                messageType === 'group' ?'bg-blue-500 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Message Groupe
            </button>
          </div>

          <Select
            label="Destinataire(s)"
            options={recipientOptions}
            value={selectedRecipients?.[0] || ''}
            onChange={(value) => setSelectedRecipients([value])}
            placeholder="Sélectionnez un étudiant ou parent"
            searchable
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Message
            </label>
            <textarea
              value={messageContent}
              onChange={(e) => setMessageContent(e?.target?.value)}
              placeholder="Écrivez votre message ici..."
              rows={6}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
          </div>

          <div className="flex gap-2">
            <Button
              variant="default"
              iconName="Send"
              iconPosition="right"
              onClick={handleSendMessage}
              className="flex-1"
            >
              Envoyer Message
            </Button>
            <Button variant="outline" iconName="Paperclip">
              Joindre
            </Button>
          </div>
        </div>
      </div>

      {/* Group Session Scheduling */}
      <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Icon name="Calendar" size={20} />
          Planification Sessions de Groupe
        </h3>

        <div className="space-y-4">
          <Input
            label="Titre de la Session"
            type="text"
            placeholder="Ex: Orientation Post-Bac"
          />

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Date"
              type="date"
            />
            <Input
              label="Heure"
              type="time"
            />
          </div>

          <Input
            label="Lieu"
            type="text"
            placeholder="Salle de conférence A"
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              placeholder="Décrivez l'objectif de la session..."
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
          </div>

          <Button
            variant="default"
            iconName="Plus"
            iconPosition="left"
            className="w-full"
          >
            Créer Session de Groupe
          </Button>
        </div>
      </div>

      {/* Automated Progress Reports */}
      <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Icon name="FileText" size={20} />
          Rapports de Progression Automatisés
        </h3>

        <div className="space-y-4">
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800 mb-2">
              <Icon name="Info" size={16} className="inline mr-1" />
              Les rapports sont générés automatiquement et envoyés aux parents
            </p>
          </div>

          <Select
            label="Fréquence d'Envoi"
            options={[
              { value: 'weekly', label: 'Hebdomadaire' },
              { value: 'biweekly', label: 'Bi-mensuel' },
              { value: 'monthly', label: 'Mensuel' }
            ]}
            value="monthly"
          />

          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700">Contenu du Rapport</p>
            <div className="space-y-2">
              <label className="flex items-center gap-2">
                <input type="checkbox" defaultChecked className="rounded" />
                <span className="text-sm text-gray-700">Performance académique</span>
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" defaultChecked className="rounded" />
                <span className="text-sm text-gray-700">Résultats des quiz</span>
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" defaultChecked className="rounded" />
                <span className="text-sm text-gray-700">Badges et réalisations</span>
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" className="rounded" />
                <span className="text-sm text-gray-700">Recommandations de filières</span>
              </label>
            </div>
          </div>

          <Button
            variant="outline"
            iconName="Settings"
            iconPosition="left"
            className="w-full"
          >
            Configurer les Rapports
          </Button>
        </div>
      </div>

      {/* Recent Communications */}
      <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Icon name="MessageSquare" size={20} />
          Communications Récentes
        </h3>

        <div className="space-y-3">
          {[
            { student: 'Amadou Diallo', type: 'email', subject: 'Rapport mensuel', date: '2026-02-10', status: 'sent' },
            { student: 'Fatou Sow', type: 'message', subject: 'Invitation session groupe', date: '2026-02-09', status: 'read' },
            { student: 'Moussa Ndiaye', type: 'call', subject: 'Discussion orientation', date: '2026-02-08', status: 'completed' }
          ]?.map((comm, index) => (
            <div key={index} className="p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-1">
                <p className="font-medium text-gray-900">{comm?.student}</p>
                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded capitalize">
                  {comm?.status}
                </span>
              </div>
              <p className="text-sm text-gray-700 mb-1">{comm?.subject}</p>
              <div className="flex items-center gap-2 text-xs text-gray-600">
                <Icon name={comm?.type === 'email' ? 'Mail' : comm?.type === 'call' ? 'Phone' : 'MessageSquare'} size={12} />
                <span>{new Date(comm?.date)?.toLocaleDateString('fr-FR')}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CommunicationTools;